/**
 * New-organisation modal — minimal form to seed an org row.
 *
 * Captures name, type, primary contact, and optional email-routing
 * domains. Domains add as chips; submit calls
 * POST /api/organizations which inserts the org + N domain rows in
 * one round-trip. On success, closes and toasts.
 */

import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import { useCreateOrganization } from '@/hooks/use-organizations.js';

interface Props {
  open: boolean;
  onClose: () => void;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DOMAIN_RE = /^[a-z0-9.-]+\.[a-z]{2,}$/i;

export function NewOrganizationDialog({ open, onClose }: Props) {
  const create = useCreateOrganization();

  const [name, setName] = useState('');
  const [type, setType] = useState<'client' | 'internal'>('client');
  const [primaryContactName, setPrimaryContactName] = useState('');
  const [primaryContactEmail, setPrimaryContactEmail] = useState('');
  const [primaryContactPhone, setPrimaryContactPhone] = useState('');
  const [domains, setDomains] = useState<string[]>([]);
  const [domainDraft, setDomainDraft] = useState('');
  const [domainError, setDomainError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName('');
      setType('client');
      setPrimaryContactName('');
      setPrimaryContactEmail('');
      setPrimaryContactPhone('');
      setDomains([]);
      setDomainDraft('');
      setDomainError(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !create.isPending) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, create.isPending, onClose]);

  if (!open) return null;

  function commitDomain(): boolean {
    const lc = domainDraft.trim().toLowerCase();
    if (!lc) return true;
    if (!DOMAIN_RE.test(lc)) {
      setDomainError(`"${domainDraft}" doesn't look like a valid domain`);
      return false;
    }
    if (domains.includes(lc)) {
      setDomainError(`${lc} is already on the list`);
      return false;
    }
    setDomains([...domains, lc]);
    setDomainDraft('');
    setDomainError(null);
    return true;
  }

  function removeDomain(d: string) {
    setDomains(domains.filter((x) => x !== d));
  }

  function handleSubmit() {
    if (!name.trim()) {
      toast.error('Name is required');
      return;
    }
    // Auto-commit a pending domain so submit-while-typing isn't lost.
    const finalDomains = (() => {
      const lc = domainDraft.trim().toLowerCase();
      if (!lc) return domains;
      if (!DOMAIN_RE.test(lc) || domains.includes(lc)) return domains;
      return [...domains, lc];
    })();

    if (
      primaryContactEmail.trim() &&
      !EMAIL_RE.test(primaryContactEmail.trim())
    ) {
      toast.error("Primary contact email doesn't look valid");
      return;
    }

    create.mutate(
      {
        name: name.trim(),
        type,
        timezone: 'Australia/Sydney',
        primaryContactName: primaryContactName.trim() || undefined,
        primaryContactEmail: primaryContactEmail.trim().toLowerCase() || undefined,
        primaryContactPhone: primaryContactPhone.trim() || undefined,
        domains: finalDomains,
      },
      {
        onSuccess: () => {
          toast.success('Organisation created', {
            description: `${name} is ready for contracts.`,
          });
          onClose();
        },
      }
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 dialog-in-backdrop"
      onClick={create.isPending ? undefined : onClose}
    >
      <div
        className="w-full max-w-lg mx-4 rounded-lg border border-ink-600 bg-ink-850 shadow-xl max-h-[90vh] overflow-y-auto dialog-in-panel"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="new-org-title"
      >
        <header className="flex items-start justify-between px-5 pt-4 pb-3 border-b border-ink-700">
          <h2 id="new-org-title" className="text-[15px] font-semibold text-ink-100">
            New organisation
          </h2>
          <button
            onClick={onClose}
            disabled={create.isPending}
            className="text-ink-300 hover:text-ink-100 focus-ring rounded-md p-1 disabled:opacity-50"
            aria-label="Close dialog"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="px-5 py-4 space-y-3">
          <Field label="Name *">
            <Input
              type="text"
              value={name}
              disabled={create.isPending}
              onChange={(e) => setName(e.target.value)}
              placeholder="Acme Pty"
            />
          </Field>

          <Field label="Type">
            <div className="flex gap-1.5">
              {(['client', 'internal'] as const).map((t) => (
                <button
                  key={t}
                  onClick={() => setType(t)}
                  disabled={create.isPending}
                  className={
                    'h-7 px-3 rounded-md text-[12.5px] focus-ring border ' +
                    (type === t
                      ? 'bg-ink-700 border-ink-500 text-ink-100'
                      : 'bg-ink-800 border-ink-600 text-ink-300 hover:text-ink-100')
                  }
                >
                  {t}
                </button>
              ))}
            </div>
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Primary contact name">
              <Input
                type="text"
                value={primaryContactName}
                disabled={create.isPending}
                onChange={(e) => setPrimaryContactName(e.target.value)}
              />
            </Field>
            <Field label="Primary contact email">
              <Input
                type="email"
                value={primaryContactEmail}
                disabled={create.isPending}
                onChange={(e) => setPrimaryContactEmail(e.target.value)}
              />
            </Field>
          </div>

          <Field label="Primary contact phone">
            <Input
              type="tel"
              value={primaryContactPhone}
              disabled={create.isPending}
              onChange={(e) => setPrimaryContactPhone(e.target.value)}
            />
          </Field>

          <Field label="Email-routing domains">
            {domains.length > 0 && (
              <ul className="flex flex-wrap gap-1.5 mb-2">
                {domains.map((d) => (
                  <li
                    key={d}
                    className="inline-flex items-center gap-1.5 rounded-md bg-ink-800 border border-ink-600 pl-2 pr-1 py-0.5 text-[12.5px] text-ink-100 mono"
                  >
                    {d}
                    <button
                      onClick={() => removeDomain(d)}
                      disabled={create.isPending}
                      className="text-ink-300 hover:text-ink-100 focus-ring rounded p-0.5 disabled:opacity-50"
                      aria-label={`Remove ${d}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <Input
              type="text"
              placeholder="acme.com  +  Enter"
              value={domainDraft}
              disabled={create.isPending}
              onChange={(e) => {
                setDomainDraft(e.target.value);
                if (domainError) setDomainError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ',') {
                  e.preventDefault();
                  commitDomain();
                }
              }}
            />
            {domainError && (
              <p className="text-[12px] text-danger-400 mt-1.5" role="alert">
                {domainError}
              </p>
            )}
          </Field>
        </div>

        <footer className="flex items-center justify-end gap-2 px-5 py-3 border-t border-ink-700">
          <Button
            variant="secondary"
            size="sm"
            onClick={onClose}
            disabled={create.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSubmit}
            disabled={create.isPending || !name.trim()}
          >
            {create.isPending ? 'Creating…' : 'Create organisation'}
          </Button>
        </footer>
      </div>
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-[0.06em] text-ink-300 mb-1.5">
        {label}
      </span>
      {children}
    </label>
  );
}
